import React, {useCallback} from "react";
import {
	FooterActionsWrapper,
	FooterButtonsWrapper,
	PrimaryButton,
	SmallContainer,
} from "components/Common";
import {useTranslation} from "react-i18next";
import {IconArrow} from "components/Icons";
import {theme} from "assets/theme";
import {AdobeTrackActionActions, AnswerType, PlayerStats} from "modules/enums";
import {useDispatch, useSelector} from "react-redux";
import {
	getActiveContest,
	getActiveContestCategory,
	getContestResult,
	getContestResultRequestState,
	getCurrentQuestion,
	getCurrentQuestionIndex,
	getCurrentTimerTimeLimit,
	getIsButtonsDisabled,
	getTimerTime,
} from "modules/selectors";
import {answerQuestion} from "modules/actions";
import {AdobeAnalyticService} from "modules/utils/Services/AdobeAnalyticService";
import {Exists} from "components/Exists";
import {Preloader} from "components/Preloader";
import {getLocallyTranslatedCategory} from "modules/utils/helpers";
import {get, filter, eq} from "lodash";
import {NUM_CHANCES} from "modules/constants";
import {createFanHubEvent, EventGUID} from "modules/utils/Services/analytics";

export const ActionsFooter: React.FC = () => {
	const {t} = useTranslation();
	const dispatch = useDispatch();
	const {isLoading} = useSelector(getContestResultRequestState);
	const currentTimerTimeLimit = useSelector(getCurrentTimerTimeLimit);
	const question = useSelector(getCurrentQuestion);
	const timeToQuestion = get(question, "timeToQuestion", 10);
	const timeLimitsNotSame = !eq(timeToQuestion, currentTimerTimeLimit);
	const isButtonsDisabled = useSelector(getIsButtonsDisabled) || isLoading || timeLimitsNotSame;
	const category = useSelector(getActiveContestCategory);
	const timeToEnd = useSelector(getTimerTime);
	const contest = useSelector(getActiveContest);
	const contestResult = useSelector(getContestResult);
	const predictions = get(contestResult, "predictions", []);
	const inCorrectPredictions = filter(predictions, (prd) => eq(prd.isCorrect, false));
	const questionIndex = useSelector(getCurrentQuestionIndex);

	const answerHandler = useCallback(
		(e: React.SyntheticEvent<HTMLButtonElement>) => {
			const answerType = e.currentTarget.dataset.answer as AnswerType;
			if (!answerType) {
				return;
			}
			dispatch(
				answerQuestion({
					answer: answerType,
					time: timeToEnd ?? 10,
				})
			);

			const getNewType = (type: AnswerType) => {
				if (eq(type, AnswerType.More)) {
					return "higher";
				} else {
					return "lower";
				}
			};

			createFanHubEvent(
				questionIndex === 1 ? EventGUID.GameplayStart : EventGUID.GameplayAction
			);

			AdobeAnalyticService.TrackAction({
				"action.name": AdobeTrackActionActions.SendAnswerClick.replace(
					"--X--",
					getNewType(answerType)
				),
				"action.element.text": getNewType(answerType),
				"action.element.targetUrl": window.location.href,
				"action.element.index": answerType === AnswerType.More ? 0 : 1,
				"gaming.mol.timer": timeToEnd ?? 10,
				"action.element.sectionIndex": questionIndex,
				"gaming.dd.gameDate": AdobeAnalyticService.getTodayDateForAnalytics(),
				"gaming.mol.category": getLocallyTranslatedCategory(
					contest?.category as PlayerStats
				),
			});
		},
		[dispatch, timeToEnd, contest, questionIndex]
	);

	if (eq(inCorrectPredictions.length, NUM_CHANCES)) {
		return <Preloader />;
	}

	return (
		<FooterActionsWrapper>
			<SmallContainer>
				<FooterButtonsWrapper>
					<Exists when={isLoading}>
						<Preloader />
					</Exists>

					<Exists when={!isLoading}>
						<React.Fragment>
							<PrimaryButton
								data-answer={AnswerType.Less}
								onClick={answerHandler}
								className="white"
								disabled={isButtonsDisabled}>
								<span>{t(`page.game.less.${category}`)}</span>
								<IconArrow to="bottom" color={theme.colors.brand.primary} />
							</PrimaryButton>
							<PrimaryButton
								data-answer={AnswerType.More}
								onClick={answerHandler}
								disabled={isButtonsDisabled}>
								<span>{t(`page.game.more.${category}`)}</span>
								<IconArrow />
							</PrimaryButton>
						</React.Fragment>
					</Exists>
				</FooterButtonsWrapper>
			</SmallContainer>
		</FooterActionsWrapper>
	);
};
